import { DateTime } from 'luxon';
import { Nutrients } from '@cf-shared/data-model/food';
import { Log_Meal, mealSlotKey, pickOneMealPerSlot } from '@cf-shared/data-model/log-meal';
import { LogRecordState } from '@cf-shared/data-model/user-log';

/**
 * Just enough of a meal to find it in a day and to know which slot it occupies.
 *
 * Not the whole document, because the meal being edited is not always IN the day:
 * `edit-meal` builds a new meal with an id and a name and does not save it until
 * its first food lands, so the portion screen is open for that first food while the
 * meal exists nowhere but in memory.
 */
export type EditedMealRef = Pick<Log_Meal, '_id' | 'name'>;

/**
 * What the day's nutrients WILL BE once the meal being edited is saved.
 *
 * The portion screen's `Day` scope needs a figure the day entry cannot give it:
 * `Log_DayStart.nutrients` is rewritten only when something is saved, so it knows
 * nothing about the amount the user is typing right now. So the day is added up
 * here instead, from the meals themselves, following the same rules the app's own
 * day total follows -- see `updateDayValuesInMemory` in `user-db.service.ts`.
 *
 * Those rules, both of which matter:
 *
 * - **One meal per slot.** A day can legitimately hold two documents for the same
 *   meal, and neither may be deleted. Every place that totals a day collapses them
 *   with `pickOneMealPerSlot` first; summing both is what once made a customer see
 *   a day total that no combination of his visible meals added up to (#4728).
 * - **A skipped meal is food nobody ate**, so it adds nothing.
 *
 * The meal being edited is then left OUT and `mealTotalNutrients` put in its
 * place. That value is already this meal's ingredients plus the pending food, so
 * the result is the day as it stands with the typed amount included -- not the day
 * plus a second copy of a meal it already counted.
 *
 * **The edited meal is excluded by SLOT, not by document id**, which matters
 * exactly when the day holds a duplicate of it: excluding only the id would leave
 * the twin's stored figures in the sum AND add the running total on top, counting
 * that one meal twice.
 *
 * **But only when the edited document is the one that actually decides its slot.**
 * `pickOneMealPerSlot` keeps one document per slot, and editing a document it
 * DROPPED changes nothing about the day: the winner is chosen on state and time,
 * neither of which typing an amount alters, so after saving the same twin still
 * wins and the same figures still count. Replacing the slot in that case would
 * show a day total the app will never produce -- and, when the dropped document is
 * a skipped one, would silently zero a meal the user really ate.
 *
 * Kept as a plain function, apart from the page, because it is the one piece of
 * arithmetic here that can be wrong without anything looking broken: a day total
 * that quietly double-counts a meal still renders as a perfectly ordinary bar.
 *
 * @param dayMeals every meal the day holds, duplicates and skipped ones included
 *   -- both are handled here rather than by the caller, so no caller can forget.
 * @param editedMeal the meal being edited, whose slot is replaced by
 *   `mealTotalNutrients`. Its identity, not the whole document, because a meal
 *   still being built is not in the day's list at all. Undefined when no meal is
 *   named, in which case every meal counts and the running total is added on top.
 * @param mealTotalNutrients the edited meal's running total, the typed amount
 *   included.
 * @param referenceTime any time on the day being shown. It only supplies the time
 *   zone the meals' wall-clock times are read in, which is what decides slot order.
 */
export function sumDayNutrientsWithEditedMeal(
  dayMeals: Log_Meal[],
  editedMeal: EditedMealRef | undefined,
  mealTotalNutrients: Nutrients,
  referenceTime: DateTime,
): Nutrients {
  const meals = dayMeals || [];
  const kept = pickOneMealPerSlot(meals, referenceTime).kept;

  // the meal as the day currently holds it -- if it holds it at all. A meal being
  // built has an id and a name before it is saved anywhere, so being absent here is
  // ordinary rather than a mistake.
  const editedInDay = editedMeal === undefined
    ? undefined
    : meals.find(m => m._id === editedMeal._id);

  // Whether the edited meal decides what its slot contributes, which is the
  // question that says whether to replace that slot with the running total:
  //  - it is the slot's kept winner        -> yes
  //  - it is a duplicate the rule DROPS    -> no; editing it cannot reach the day
  //  - it is not in the day at all         -> yes; nothing of it is in the sum, so
  //    counting the slot's current occupant AND the running total would count one
  //    slot twice, which saving can never produce
  //
  // That last case is the best answer available rather than a certainty. Which
  // document wins a slot is decided by TIME, not by which was written last, so a
  // same-named meal already logged LATER in the day keeps the slot after the save
  // and the new meal's food drops out of the day total. Predicting that here would
  // mean knowing the new meal's time, which this path is not given. Replacing the
  // slot is right whenever the new meal wins and never double-counts when it does
  // not; counting both was wrong in every case.
  const editedDecidesItsSlot = editedMeal !== undefined
    && (editedInDay === undefined || kept.some(m => m._id === editedMeal._id));
  const replacedSlot = editedDecidesItsSlot ? mealSlotKey(editedMeal) : undefined;

  const total: Nutrients = {};

  // for every slot the day holds that was actually eaten, except the one being replaced
  for (const meal of kept) {
    if (replacedSlot !== undefined && mealSlotKey(meal) === replacedSlot) continue;
    if (meal.state === LogRecordState.Skipped) continue;
    for (const tag in meal.nutrients || {}) {
      total[tag] = (total[tag] || 0) + meal.nutrients[tag];
    }
  }

  // and this meal as it stands right now. Not when it is skipped -- saving will
  // not add it to the day either, so the card must not promise a day that will not
  // happen -- and not when the edit cannot reach the day's total at all, which is
  // the dropped-duplicate case above. With no meal selected there is nothing to
  // replace and the pending food is simply added.
  // the skip is read from the day's own copy: the caller passes an identity, which
  // carries no state, and a meal that is not in the day yet cannot have been skipped
  const addsToTheDay = editedMeal === undefined
    || (editedDecidesItsSlot && editedInDay?.state !== LogRecordState.Skipped);
  if (addsToTheDay) {
    for (const tag in mealTotalNutrients || {}) {
      total[tag] = (total[tag] || 0) + mealTotalNutrients[tag];
    }
  }

  return total;
}
